import { useCallback, useState } from "react";
import { addSeats, type BillingContext } from "@/lib/api/organizations";

/// Paddle refuses an update to a subscription with a pending change, so a
/// cancelling or paused one cannot grow.
export function canBuySeats(billing: BillingContext | null): boolean {
  const subscription = billing?.subscription;
  return Boolean(
    billing?.can_manage_billing &&
    billing.processor_customer_id &&
    subscription &&
    subscription.status !== "paused" &&
    subscription.scheduled_change?.action !== "cancel",
  );
}

/// Buying alone floors at the higher of what is paid for and what is occupied;
/// with `allowDecrease` a paid seat may be given up, so only the roster holds.
export function useSeatPurchase(
  orgId: string,
  held: number,
  occupied = 0,
  allowDecrease = false,
) {
  const floor = allowDecrease
    ? Math.max(occupied, 1)
    : Math.max(held, occupied);
  // A two-way editor opens where you are; an add-only one opens on the first
  // number worth confirming.
  const start = allowDecrease && floor < held ? held : floor + 1;
  const [open, setOpen] = useState(false);
  const [wanted, setWanted] = useState(start);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openEditor = useCallback(() => {
    setWanted(start);
    setError(null);
    setOpen(true);
  }, [start]);

  const close = useCallback(() => setOpen(false), []);

  /// The count Paddle now holds, or `null` when nothing moved.
  const confirm = useCallback(async (): Promise<number | null> => {
    setBusy(true);
    setError(null);
    const result = await addSeats(orgId, wanted);
    setBusy(false);
    if (!result) {
      setError(
        wanted < held
          ? "The seat change could not be saved. Please try again."
          : "The seats could not be added. If a charge was declined, update your card and try again.",
      );
      return null;
    }
    setOpen(false);
    return result.seats;
  }, [orgId, wanted, held]);

  return {
    open,
    openEditor,
    close,
    floor,
    wanted,
    setWanted,
    busy,
    error,
    confirm,
  };
}
